import React, { useState } from 'react';
import { PeriodConfig } from '../types';
import { triggerAnalyticsRefresh } from '../hooks/useAnalyticsData';

interface PeriodSelectorProps {
  period: PeriodConfig;
  onChange: (period: PeriodConfig) => void;
  onSwitchToClassic?: () => void;
}

const QUICK_PERIODS = [
  { label: 'Live', config: { type: 'live' as const } },
  { label: '7 Days', config: { type: 'period' as const, days: '7d' as const } },
  { label: '14 Days', config: { type: 'period' as const, days: '14d' as const } },
  { label: '30 Days', config: { type: 'period' as const, days: '30d' as const } },
  { label: '90 Days', config: { type: 'period' as const, days: '90d' as const } },
];

const isActive = (current: PeriodConfig, target: PeriodConfig): boolean => {
  if (current.type !== target.type) return false;
  if (current.type === 'period' && target.type === 'period') return current.days === target.days;
  return current.type === target.type;
};

const PeriodSelector: React.FC<PeriodSelectorProps> = ({ period, onChange, onSwitchToClassic }) => {
  const [showCustom, setShowCustom] = useState(period.type === 'custom');
  const [fromDate, setFromDate] = useState(period.type === 'custom' ? period.from : '');
  const [toDate, setToDate] = useState(period.type === 'custom' ? period.to : '');
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const isCustomActive = period.type === 'custom';

  const handleRefresh = () => {
    setRefreshing(true);
    triggerAnalyticsRefresh();
    setLastRefresh(new Date());
    // Brief spinner animation; the hook's loading state drives actual fetching.
    setTimeout(() => setRefreshing(false), 600);
  };

  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        {/* Refresh — positioned before the Period controls per the UX request */}
        <button
          onClick={handleRefresh}
          title={`Last updated ${lastRefresh.toLocaleTimeString()}`}
          className="flex items-center gap-1.5 px-3 py-2 sm:py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition-all shadow-sm"
        >
          <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>

        <span className="hidden sm:inline text-xs text-gray-500 uppercase tracking-wider font-medium ml-1">Period:</span>

        {QUICK_PERIODS.map((opt) => (
          <button
            key={opt.label}
            onClick={() => {
              onChange(opt.config);
              setShowCustom(false);
            }}
            className={`px-3 sm:px-3.5 py-2 sm:py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
              isActive(period, opt.config)
                ? 'bg-[#FF9C15] text-black shadow-lg shadow-[#FF9C15]/20'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
            }`}
          >
            {opt.label}
          </button>
        ))}

        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`px-3 sm:px-3.5 py-2 sm:py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
            isCustomActive
              ? 'bg-[#FF9C15] text-black shadow-lg shadow-[#FF9C15]/20'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
          }`}
        >
          Custom
        </button>

        {/* Switch to Classic — pushed to the right on >=sm, wraps on mobile */}
        {onSwitchToClassic && (
          <button
            onClick={onSwitchToClassic}
            className="sm:ml-auto px-3 py-2 sm:py-1.5 text-xs font-medium text-gray-400 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-all"
          >
            <span className="sm:hidden">Classic</span>
            <span className="hidden sm:inline">Switch to Classic</span>
          </button>
        )}
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="flex-1 sm:flex-none min-w-[140px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF9C15] focus:outline-none [color-scheme:dark]"
          />
          <span className="text-gray-500 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="flex-1 sm:flex-none min-w-[140px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF9C15] focus:outline-none [color-scheme:dark]"
          />
          <button
            onClick={() => {
              if (fromDate && toDate) {
                onChange({ type: 'custom', from: fromDate, to: toDate });
              }
            }}
            disabled={!fromDate || !toDate}
            className={`px-5 py-2 text-xs font-semibold rounded-lg transition-all ${
              fromDate && toDate
                ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 shadow-lg'
                : 'bg-white/5 text-gray-600 cursor-not-allowed'
            }`}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
